import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  TablePagination,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import { 
  Search, 
  Refresh, 
  Visibility,
  Assessment,
  Info,
  CheckCircle,
  Warning,
  CalendarToday,
  Receipt,
  AttachMoney,
  TrendingUp,
  AccountBalance,
  Business,
  Description,
  MonetizationOn
} from '@mui/icons-material';

const PayoutReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Search form state
  const [searchForm, setSearchForm] = useState({
    payout: '',
    fromDate: '',
    toDate: '',
  });

  // Sample payout data
  const [payoutData] = useState([
    {
      id: 1,
      payout: 'PO001',
      directAmount: 125000,
      differenceAmount: 5000,
      grossAmount: 130000,
      tdsAmount: 13000,
      processingFees: 1000,
      advance: 10000,
      adjustedAmount: 106000,
      netAmount: 106000,
      closingDate: '2024-01-15',
    },
    {
      id: 2,
      payout: 'PO002',
      directAmount: 85000,
      differenceAmount: 3000,
      grossAmount: 88000,
      tdsAmount: 8800,
      processingFees: 800,
      advance: 5000,
      adjustedAmount: 73400,
      netAmount: 73400,
      closingDate: '2024-01-20',
    },
    {
      id: 3,
      payout: 'PO003',
      directAmount: 200000,
      differenceAmount: 8000,
      grossAmount: 208000,
      tdsAmount: 20800,
      processingFees: 1500,
      advance: 15000,
      adjustedAmount: 170700,
      netAmount: 170700,
      closingDate: '2024-01-25',
    },
    {
      id: 4,
      payout: 'PO004',
      directAmount: 150000,
      differenceAmount: 6000,
      grossAmount: 156000,
      tdsAmount: 15600,
      processingFees: 1200,
      advance: 12000,
      adjustedAmount: 127200,
      netAmount: 127200,
      closingDate: '2024-02-01',
    },
    {
      id: 5,
      payout: 'PO005',
      directAmount: 95000,
      differenceAmount: 4000,
      grossAmount: 99000,
      tdsAmount: 9900,
      processingFees: 900,
      advance: 8000,
      adjustedAmount: 80200,
      netAmount: 80200,
      closingDate: '2024-02-05',
    },
    {
      id: 6,
      payout: 'PO006',
      directAmount: 175000,
      differenceAmount: 7000,
      grossAmount: 182000,
      tdsAmount: 18200,
      processingFees: 1400,
      advance: 14000,
      adjustedAmount: 148400,
      netAmount: 148400,
      closingDate: '2024-02-10',
    },
  ]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleSearchFormChange = (field, value) => {
    setSearchForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    console.log('Searching with data:', searchForm);
    setSuccessMessage(`Search completed! Found ${getFilteredPayoutData().length} payout record(s).`);
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleReset = () => {
    setSearchForm({
      payout: '',
      fromDate: '',
      toDate: '',
    });
    setSuccessMessage('');
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewAction = (payoutId) => {
    console.log('Viewing payout:', payoutId);
    // Implement view functionality
  };

  const getFilteredPayoutData = () => {
    let filteredData = payoutData;

    // Filter by payout
    if (searchForm.payout.trim()) {
      filteredData = filteredData.filter(item =>
        item.payout.toLowerCase().includes(searchForm.payout.toLowerCase())
      );
    }

    // Filter by date range
    if (searchForm.fromDate && searchForm.toDate) {
      filteredData = filteredData.filter(item => {
        const itemDate = new Date(item.closingDate);
        const fromDate = new Date(searchForm.fromDate);
        const toDate = new Date(searchForm.toDate);
        return itemDate >= fromDate && itemDate <= toDate;
      });
    }

    return filteredData;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const getFilteredByStatus = (status) => {
    return payoutData.filter(payout => payout.status === status);
  };

  const getSearchSummary = () => {
    const total = getFilteredPayoutData().length;
    const totalAmount = getFilteredPayoutData().reduce((sum, payout) => sum + payout.netAmount, 0);
    return `Showing ${total} payout record(s) with total net amount of ${formatCurrency(totalAmount)}`;
  };

  // Summary calculations
  const totalPayouts = payoutData.length;
  const totalNetAmount = payoutData.reduce((sum, payout) => sum + payout.netAmount, 0);
  const totalGrossAmount = payoutData.reduce((sum, payout) => sum + payout.grossAmount, 0);
  const totalTdsAmount = payoutData.reduce((sum, payout) => sum + payout.tdsAmount, 0);
  const avgPayoutAmount = totalNetAmount / totalPayouts;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={2} mb={4}>
        <Assessment sx={{ color: '#1976d2', fontSize: 32 }} />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: '#1976d2' }}>
          Payout Report Management
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle sx={{ fontSize: 20 }} />
            {successMessage}
          </Box>
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e3f2fd', 
            border: '1px solid #90caf9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <Receipt sx={{ color: '#1976d2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Total Payouts
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  {totalPayouts}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#e8f5e9', 
            border: '1px solid #c8e6c9',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <AttachMoney sx={{ color: '#2e7d32', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Net Amount
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  ₹{(totalNetAmount / 100000).toFixed(1)}L
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#fff3e0', 
            border: '1px solid #ffcc02',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <TrendingUp sx={{ color: '#f57c00', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Gross Amount
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  ₹{(totalGrossAmount / 100000).toFixed(1)}L
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#ffebee', 
            border: '1px solid #ffcdd2',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <AccountBalance sx={{ color: '#d32f2f', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  TDS Amount
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#d32f2f', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  ₹{(totalTdsAmount / 100000).toFixed(1)}L
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ 
            p: 3, 
            backgroundColor: '#f3e5f5', 
            border: '1px solid #e1bee7',
            height: '120px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ height: '100%' }}>
              <MonetizationOn sx={{ color: '#7b1fa2', fontSize: 32, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', lineHeight: 1.2 }}>
                  Avg. Amount
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#7b1fa2', fontSize: '1.75rem', lineHeight: 1.2 }}>
                  ₹{(avgPayoutAmount / 1000).toFixed(0)}K
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Card */}
      <Card sx={{ mb: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Description sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Search & Filter Payout Reports
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Info sx={{ fontSize: 20 }} />
              Search and filter payout reports by payout ID and date range
            </Box>
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Payout ID"
                value={searchForm.payout}
                onChange={(e) => handleSearchFormChange('payout', e.target.value)}
                placeholder="Enter payout ID"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Receipt sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="From Date"
                type="date"
                value={searchForm.fromDate}
                onChange={(e) => handleSearchFormChange('fromDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="To Date"
                type="date"
                value={searchForm.toDate}
                onChange={(e) => handleSearchFormChange('toDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday sx={{ color: '#666' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Search />}
                  onClick={handleSearch}
                  fullWidth
                  sx={{
                    minHeight: '56px',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  Search
                </Button>
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<Refresh />}
                  onClick={handleReset}
                  sx={{ 
                    minWidth: 160,
                    py: 1.5,
                    px: 3,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                    },
                  }}
                >
                  Reset Filters
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payout Report Table */}
      <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <Business sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
              Payout Report Data
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Info sx={{ fontSize: 20 }} />
              {getSearchSummary()}
            </Box>
          </Alert>
          
          {getFilteredPayoutData().length > 0 ? (
            <TableContainer sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Action
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      S.No
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Payout Info
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Direct Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Difference Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Gross Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      TDS Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Processing Fees
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Advance
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Adjusted Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Net Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '14px', color: '#1976d2' }}>
                      Closing Date
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getFilteredPayoutData()
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((payout, index) => (
                      <TableRow key={payout.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleViewAction(payout.id)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {page * rowsPerPage + index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Receipt sx={{ fontSize: 16, color: '#666' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              {payout.payout}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AttachMoney sx={{ fontSize: 16, color: '#666' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(payout.directAmount)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <TrendingUp sx={{ fontSize: 16, color: '#2e7d32' }} />
                            <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600 }}>
                              {formatCurrency(payout.differenceAmount)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AttachMoney sx={{ fontSize: 16, color: '#f57c00' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                              {formatCurrency(payout.grossAmount)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AccountBalance sx={{ fontSize: 16, color: '#d32f2f' }} />
                            <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 600 }}>
                              {formatCurrency(payout.tdsAmount)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <MonetizationOn sx={{ fontSize: 16, color: '#ed6c02' }} />
                            <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 600 }}>
                              {formatCurrency(payout.processingFees)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Business sx={{ fontSize: 16, color: '#0288d1' }} />
                            <Typography variant="body2" sx={{ color: 'info.main', fontWeight: 600 }}>
                              {formatCurrency(payout.advance)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AttachMoney sx={{ fontSize: 16, color: '#666' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {formatCurrency(payout.adjustedAmount)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <AttachMoney sx={{ fontSize: 16, color: '#2e7d32' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                              {formatCurrency(payout.netAmount)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <CalendarToday sx={{ fontSize: 16, color: '#666' }} />
                            <Typography variant="body2">
                              {payout.closingDate}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
            </Table>

              {/* Pagination */}
              <TablePagination
                
                component="div"
                count={getFilteredPayoutData().length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          ) : (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Warning sx={{ fontSize: 20 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    No Payout Records Found
                  </Typography>
                  <Typography variant="body2">
                    No payout records match your search criteria. Try adjusting your filters.
                  </Typography>
                </Box>
              </Box>
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PayoutReport;